/**
 * Deferred image loading for markup that is in the DOM but not on screen.
 *
 * `visibility: hidden` and `opacity: 0` containers still generate layout boxes, so
 * vanilla-lazyload's IntersectionObserver considers their images to be in view and
 * downloads them during page load. The header nav dropdowns are built that way (see
 * `blocks/header/_header-menu-item.liquid`, which uses `invisible` so the panel can
 * transition), and measured at ~110MB of decoded image memory on every page — none
 * of it visible unless the shopper opens a menu.
 *
 * Images rendered with `manual_load: true` (see `snippets/image/responsive-image.liquid`)
 * keep their real sources in data attributes and omit the `lazy` class, so
 * vanilla-lazyload ignores them entirely. This attaches those sources the first time
 * their container is actually revealed.
 */

import { capLazySizes } from './image-density'

// `:not(.lazy)` is what keeps this off vanilla-lazyload's own images: those still
// carry the class and must stay under its control, or opening a long modal would
// eagerly load everything inside it.
const DEFERRED_SELECTOR = 'img[data-src]:not(.lazy):not([data-attached])'

export function loadDeferredImages(root: ParentNode | null | undefined): void {
  if (!root) return

  root.querySelectorAll<HTMLImageElement>(DEFERRED_SELECTOR).forEach((img) => {
    if (!img.dataset.src) return
    capLazySizes(img)
    const { src, srcset, sizes } = img.dataset

    if (sizes) img.sizes = sizes
    if (srcset) img.srcset = srcset
    img.src = src
    img.dataset.attached = 'true'
  })
}
